import { useState } from 'react';
import { registerCreditCardToAccount } from 'src/components/Account/payment.utils';
import { pushError } from 'src/utilities/notifications';
import { useInitializeCreditCardIframe } from './useInitializeCreditCardIframe';
import { getIframeContainerId } from './CreditCardFormIframe.utils';

export interface UseCreditCardIframeProps {
  corpId: string;
  cardId?: string;
  onSuccess?: () => void;
  onError?: () => void;
  idSuffix: string;
  onIframeLoaded?: () => void;
  onInvalidCard?: () => void;
  onValidCard?: () => void;
}

export type UseCreditCardIframeReturn = {
  onIframeSubmit: () => Promise<void>;
  iframeContainerId: string;
  cardIsValid: boolean;
  isSubmitting: boolean;
  isSubmitSuccessful: boolean;
  isLoadingIframe: boolean;
  isLoadingComponent: boolean;
};

export const useCreditCardIframe = ({
  corpId,
  cardId,
  onSuccess,
  onError,
  idSuffix,
  onIframeLoaded,
  onInvalidCard,
  onValidCard,
}: UseCreditCardIframeProps): UseCreditCardIframeReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const iframeContainerId = getIframeContainerId(idSuffix);
  const { tokenize, cardIsValid, isLoadingIframe, isLoadingComponent } =
    useInitializeCreditCardIframe({
      iframeContainerId,
      onIframeLoaded,
      onInvalidCard,
      onValidCard,
    });

  const onIframeSubmit = (): Promise<void> => {
    setIsSubmitting(true);
    setIsSubmitSuccessful(false);

    return registerCreditCardToAccount({
      cardId,
      corpId,
      onError: () => {
        pushError(
          'There was an error processing the payment. Please try again.'
        );
        onError?.();
      },
      onSuccess: () => {
        setIsSubmitSuccessful(true);
        onSuccess?.();
      },
      onFinally: () => {
        setIsSubmitting(false);
      },
      tokenizeCallback: tokenize,
    });
  };

  return {
    onIframeSubmit,
    iframeContainerId,
    cardIsValid,
    isSubmitting,
    isSubmitSuccessful,
    isLoadingIframe,
    isLoadingComponent,
  };
};
